import FirebaseAPI from '../firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  limit as limitClause,
  startAfter as startAfterClause,
} from 'firebase/firestore';

const firebase = new FirebaseAPI();
const BLOG_COLLECTION = 'metadata/posts/blog';

async function fetchPostsPage({ pageSize = 10, cursor = null } = {}) {
  try {
    const constraints = [orderBy('createdAt', 'desc'), limitClause(pageSize)];
    if (cursor) {
      constraints.splice(1, 0, startAfterClause(cursor));
    }
    const postsQuery = query(collection(firebase.db, BLOG_COLLECTION), ...constraints);
    const snapshot = await getDocs(postsQuery);
    const posts = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
    const lastDoc = snapshot.docs.length ? snapshot.docs[snapshot.docs.length - 1] : null;
    return {
      posts,
      cursor: lastDoc,
      hasMore: snapshot.size === pageSize,
    };
  } catch (error) {
    console.error('An error occurred while fetching paginated blog posts:', error);
    throw error;
  }
}

async function fetchAllPosts() {
  const allPosts = [];
  let cursor = null;
  let hasMore = true;
  while (hasMore) {
    const { posts, cursor: nextCursor, hasMore: more } = await fetchPostsPage({
      pageSize: 25,
      cursor,
    });
    allPosts.push(...posts);
    cursor = nextCursor;
    hasMore = more && !!cursor;
    if (!cursor) {
      break;
    }
  }
  return allPosts;
}

export const blogService = {
  // Tüm blog yazılarını getir
  async getAllPosts() {
    try {
      return await fetchAllPosts();
    } catch (error) {
      console.error('An error occurred while fetching blog posts:', error);
      throw error;
    }
  },

  async getPostsPage(options) {
    return fetchPostsPage(options);
  },

  async getLatestPosts(limit = 5) {
    const { posts } = await fetchPostsPage({ pageSize: limit });
    return posts;
  },

  // Tek bir blog yazısını getir
  async getPostById(id) {
    try {
      const docRef = doc(firebase.db, BLOG_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Blog yazısı bulunamadı');
      }
    } catch (error) {
      console.error('Blog yazısı getirilirken hata oluştu:', error);
      throw error;
    }
  },

  // Yeni blog yazısı ekle
  async createPost(postData) {
    try {
      const docRef = await addDoc(collection(firebase.db, BLOG_COLLECTION), {
        ...postData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Blog yazısı eklenirken hata oluştu:', error);
      throw error;
    }
  },

  // Blog yazısını güncelle
  async updatePost(id, postData) {
    try {
      const docRef = doc(firebase.db, BLOG_COLLECTION, id);
      await updateDoc(docRef, {
        ...postData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Blog yazısı güncellenirken hata oluştu:', error);
      throw error;
    }
  },

  // Blog yazısını sil
  async deletePost(id) {
    try {
      const docRef = doc(firebase.db, BLOG_COLLECTION, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Blog yazısı silinirken hata oluştu:', error);
      throw error;
    }
  },

  // Yorumları getir
  async getComments(postId) {
    try {
      const q = query(collection(firebase.db, `${BLOG_COLLECTION}/${postId}/comments`));
      const querySnapshot = await getDocs(q);
      const comments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Her yorum için reply sayısını al
      const commentsWithReplyCount = await Promise.all(comments.map(async (comment) => {
        const repliesSnapshot = await getDocs(collection(firebase.db, `${BLOG_COLLECTION}/${postId}/comments/${comment.id}/replies`));
        return {
          ...comment,
          replyCount: repliesSnapshot.size,
          likeCount: (comment.likes || []).length
        };
      }));

      // Yorumları sırala:
      // 1. Önce like sayısına göre (azalan)
      // 2. Sonra reply sayısına göre (azalan)
      // 3. Son olarak oluşturulma tarihine göre (azalan)
      return commentsWithReplyCount.sort((a, b) => {
        if (b.likeCount !== a.likeCount) {
          return b.likeCount - a.likeCount;
        }
        if (b.replyCount !== a.replyCount) {
          return b.replyCount - a.replyCount;
        }
        return b.createdAt?.toDate() - a.createdAt?.toDate();
      });
    } catch (error) {
      console.error('An error occurred while fetching comments:', error);
      throw error;
    }
  },

  // Yorum ekle
  async addComment(postId, commentData) {
    try {
      const docRef = await addDoc(collection(firebase.db, `${BLOG_COLLECTION}/${postId}/comments`), {
        ...commentData,
        createdAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      console.error('An error occurred while adding comment:', error);
      throw error;
    }
  },

  // Yorumda like ekle/kaldır
  async toggleLike(postId, commentId, userId) {
    const commentRef = doc(firebase.db, `${BLOG_COLLECTION}/${postId}/comments/${commentId}`);
    const commentSnap = await getDoc(commentRef);
    if (!commentSnap.exists()) return;
    const data = commentSnap.data();
    const likes = data.likes || [];
    const dislikes = data.dislikes || [];
    
    // Eğer kullanıcı daha önce dislike yapmışsa, önce dislike'ı kaldır
    if (dislikes.includes(userId)) {
      const newDislikes = dislikes.filter(id => id !== userId);
      await updateDoc(commentRef, { dislikes: newDislikes });
    }

    // Like işlemini gerçekleştir
    let newLikes;
    if (likes.includes(userId)) {
      newLikes = likes.filter(id => id !== userId);
    } else {
      newLikes = [...likes, userId];
    }
    await updateDoc(commentRef, { likes: newLikes });
  },

  // Yorumda dislike ekle/kaldır
  async toggleDislike(postId, commentId, userId) {
    const commentRef = doc(firebase.db, `${BLOG_COLLECTION}/${postId}/comments/${commentId}`);
    const commentSnap = await getDoc(commentRef);
    if (!commentSnap.exists()) return;
    const data = commentSnap.data();
    const likes = data.likes || [];
    const dislikes = data.dislikes || [];
    
    // Eğer kullanıcı daha önce like yapmışsa, önce like'ı kaldır
    if (likes.includes(userId)) {
      const newLikes = likes.filter(id => id !== userId);
      await updateDoc(commentRef, { likes: newLikes });
    }

    // Dislike işlemini gerçekleştir
    let newDislikes;
    if (dislikes.includes(userId)) {
      newDislikes = dislikes.filter(id => id !== userId);
    } else {
      newDislikes = [...dislikes, userId];
    }
    await updateDoc(commentRef, { dislikes: newDislikes });
  },

  // Yorum için reply ekle
  async addReply(postId, commentId, replyData) {
    const repliesRef = collection(firebase.db, `${BLOG_COLLECTION}/${postId}/comments/${commentId}/replies`);
    await addDoc(repliesRef, {
      ...replyData,
      createdAt: serverTimestamp(),
    });
  },

  // Yorumun reply'larını getir
  async getReplies(postId, commentId) {
    const repliesRef = collection(firebase.db, `${BLOG_COLLECTION}/${postId}/comments/${commentId}/replies`);
    const q = query(repliesRef, orderBy('createdAt', 'asc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Reply güncelle
  async updateReply(postId, commentId, replyId, newContent) {
    const replyRef = doc(firebase.db, `${BLOG_COLLECTION}/${postId}/comments/${commentId}/replies/${replyId}`);
    await updateDoc(replyRef, { content: newContent });
  },

  // Reply sil
  async deleteReply(postId, commentId, replyId) {
    const replyRef = doc(firebase.db, `${BLOG_COLLECTION}/${postId}/comments/${commentId}/replies/${replyId}`);
    await deleteDoc(replyRef);
  },

  // Yorum güncelle
  async updateComment(postId, commentId, newContent) {
    const commentRef = doc(firebase.db, `${BLOG_COLLECTION}/${postId}/comments/${commentId}`);
    await updateDoc(commentRef, { content: newContent });
  },

  // Yorum sil
  async deleteComment(postId, commentId) {
    const commentRef = doc(firebase.db, `${BLOG_COLLECTION}/${postId}/comments/${commentId}`);
    await deleteDoc(commentRef);
  }
}; 